/**
 * UAT tracker rules shared by the Worker and the app (Rod, 2026-11-04).
 * Everything here is pure, so the Worker routes, the admin import preview
 * and the tests use the same statuses, limits and CSV parser.
 */

export const UAT_STATUSES = ['delivered', 'in_testing', 'passed', 'needs_fix'] as const
export type UatStatus = typeof UAT_STATUSES[number]

export const UAT_CASE_RESULTS = ['not_run', 'pass', 'fail'] as const
export type UatCaseResult = typeof UAT_CASE_RESULTS[number]

export const UAT_STATUS_LABEL: Record<UatStatus, string> = {
  delivered: 'Delivered',
  in_testing: 'In Testing',
  passed: 'Passed',
  needs_fix: 'Needs Fix',
}

/** StatusBadge tones: info for new work, pending while testing, success and danger for results. */
export const UAT_STATUS_TONE: Record<UatStatus, 'info' | 'pending' | 'success' | 'danger'> = {
  delivered: 'info',
  in_testing: 'pending',
  passed: 'success',
  needs_fix: 'danger',
}

export const UAT_CASE_RESULT_LABEL: Record<UatCaseResult, string> = {
  not_run: 'Not Run',
  pass: 'Pass',
  fail: 'Fail',
}

export const UAT_LIMITS = {
  titleChars: 200,
  descriptionChars: 2_000,
  noteChars: 2_000,
  caseChars: 1_000,
  areaChars: 120,
  itemsPerWorkspace: 200,
  casesPerItem: 500,
  /** The CSV text an admin can import at once. */
  csvChars: 500_000,
} as const

export const UAT_KEY = /^[a-z0-9][a-z0-9-]{0,79}$/
export const LINEAR_IDENTIFIER = /^[A-Z][A-Z0-9]{0,9}-[0-9]{1,7}$/

export function isUatStatus(value: unknown): value is UatStatus {
  return typeof value === 'string' && (UAT_STATUSES as readonly string[]).includes(value)
}

export function isUatCaseResult(value: unknown): value is UatCaseResult {
  return typeof value === 'string' && (UAT_CASE_RESULTS as readonly string[]).includes(value)
}

/** Counts per status, in status order, for the summary at the top of the tab. */
export function uatStatusCounts(items: ReadonlyArray<{ status: UatStatus }>): Record<UatStatus, number> {
  const counts: Record<UatStatus, number> = { delivered: 0, in_testing: 0, passed: 0, needs_fix: 0 }
  for (const item of items) if (isUatStatus(item.status)) counts[item.status] += 1
  return counts
}

/**
 * A Linear issue reference from what an admin pastes: an identifier
 * ("P-1234", any case) or an issue URL
 * ("https://linear.app/prentus/issue/P-1234/some-title"). The URL is kept
 * only when it is a linear.app issue URL.
 */
export function parseLinearIssueRef(input: string): { identifier: string; url: string | null } | null {
  const value = input.trim()
  if (!value) return null
  const plain = value.toUpperCase()
  if (LINEAR_IDENTIFIER.test(plain)) return { identifier: plain, url: null }
  let url: URL
  try { url = new URL(value) } catch { return null }
  if (url.protocol !== 'https:' || url.hostname !== 'linear.app') return null
  const match = /^\/[A-Za-z0-9_-]{1,80}\/issue\/([A-Za-z][A-Za-z0-9]{0,9}-[0-9]{1,7})(?:\/[^/?#]*)?\/?$/.exec(url.pathname)
  if (!match) return null
  const identifier = match[1].toUpperCase()
  return { identifier, url: `https://linear.app${url.pathname.replace(/\/$/, '')}` }
}

/** Trims text, normalizes line breaks, and drops control characters other than tab and line feed. */
export function normalizeUatText(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, '')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim()
}

/** One line of text (titles, areas): no line breaks, single spaces. */
export function singleLine(value: string): string {
  return normalizeUatText(value).replace(/\s+/g, ' ')
}

/** Cuts text to max UTF-16 units without leaving half of an emoji, which the database refuses. */
export function cutText(value: string, max: number): string {
  const cut = value.slice(0, max)
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut
}

// --- CSV import ------------------------------------------------------------

export interface ParsedUatCase {
  text: string
  area: string | null
}

export interface UatCsvResult {
  cases: ParsedUatCase[]
  /** The header names used, or null when the file had no recognized header. */
  caseColumn: string | null
  areaColumn: string | null
  /** Rows without case text, and rows past the per-item limit. */
  skipped: number
  /** Set when nothing could be imported. Sentence case, for the admin. */
  error: string | null
}

/**
 * Header names, normalized to lower-case letters and digits. Earlier names win.
 * "name" and "section/column" match an Asana CSV export.
 */
const CASE_HEADERS = ['testcase', 'case', 'testcasename', 'casename', 'testcasedescription', 'casedescription', 'scenario', 'testscenario', 'scenarioname', 'teststeps', 'steps', 'step', 'test', 'testname', 'title', 'testtitle', 'requirement', 'description', 'summary', 'name', 'taskname', 'task']
const AREA_HEADERS = ['area', 'functionalarea', 'module', 'feature', 'featurearea', 'component', 'section', 'sectioncolumn', 'category', 'page', 'screen', 'epic']
const EXPECTED_HEADERS = ['expectedresult', 'expectedresults', 'expected', 'expectedoutcome', 'expectedbehavior', 'expectedbehaviour']

function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function detectDelimiter(firstLine: string): string {
  const counts = [',', ';', '\t'].map((delimiter) => ({ delimiter, count: firstLine.split(delimiter).length - 1 }))
  counts.sort((a, b) => b.count - a.count)
  return counts[0].count > 0 ? counts[0].delimiter : ','
}

/** RFC 4180 rows: quoted fields, doubled quotes, line breaks inside quotes, CRLF or LF. */
export function parseCsvRows(text: string): string[][] {
  const source = text.replace(/^﻿/, '')
  const firstLine = source.split(/\r?\n/, 1)[0] || ''
  const delimiter = detectDelimiter(firstLine)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]
    if (quoted) {
      if (character === '"') {
        if (source[index + 1] === '"') { field += '"'; index += 1 }
        else quoted = false
      } else field += character
      continue
    }
    if (character === '"' && field.trim() === '') { quoted = true; field = ''; continue }
    if (character === delimiter) { row.push(field); field = ''; continue }
    if (character === '\n' || character === '\r') {
      if (character === '\r' && source[index + 1] === '\n') index += 1
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      continue
    }
    field += character
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row) }
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''))
}

function findColumn(headers: string[], names: string[], exclude: number[] = []): number {
  const normalized = headers.map(normalizeHeader)
  for (const name of names) {
    const index = normalized.findIndex((header, position) => header === name && !exclude.includes(position))
    if (index >= 0) return index
  }
  return -1
}

/**
 * Reads test cases from a CSV file. It looks for a case column (Case, Test
 * Case, Scenario, Description, Steps, Title, Name and similar) and an
 * optional area column (Area, Module, Feature, Section and similar). An
 * Expected Result column is added to the case text. A file with one column
 * and no recognized header is read as one case per line.
 */
export function parseUatCasesCsv(text: string, limit: number = UAT_LIMITS.casesPerItem): UatCsvResult {
  const empty = (error: string): UatCsvResult => ({ cases: [], caseColumn: null, areaColumn: null, skipped: 0, error })
  if (!text.trim()) return empty('The file is empty.')
  if (text.length > UAT_LIMITS.csvChars) return empty('The file is too large. Import at most 500,000 characters at a time.')
  const rows = parseCsvRows(text)
  if (!rows.length) return empty('The file has no rows.')

  const headers = rows[0].map((cell) => cell.trim())
  let caseIndex = findColumn(headers, CASE_HEADERS)
  let body = rows.slice(1)
  let caseColumn: string | null = caseIndex >= 0 ? headers[caseIndex] : null
  if (caseIndex < 0) {
    const width = Math.max(...rows.map((cells) => cells.length))
    if (width === 1) {
      // One column and no header: every line is a case.
      caseIndex = 0
      body = rows
    } else {
      return empty('No case column was found. Name one column Case, Test Case, Scenario or Description.')
    }
  }
  const areaIndex = caseColumn ? findColumn(headers, AREA_HEADERS, [caseIndex]) : -1
  const expectedIndex = caseColumn ? findColumn(headers, EXPECTED_HEADERS, [caseIndex, areaIndex]) : -1

  const cases: ParsedUatCase[] = []
  let skipped = 0
  for (const cells of body) {
    const caseText = normalizeUatText(cells[caseIndex] || '')
    if (!caseText) { skipped += 1; continue }
    if (cases.length >= limit) { skipped += 1; continue }
    const expected = expectedIndex >= 0 ? normalizeUatText(cells[expectedIndex] || '') : ''
    const full = expected ? `${caseText}\nExpected: ${expected}` : caseText
    const area = areaIndex >= 0 ? cutText(singleLine(cells[areaIndex] || ''), UAT_LIMITS.areaChars) : ''
    cases.push({ text: cutText(full, UAT_LIMITS.caseChars), area: area || null })
  }
  if (!cases.length) return { ...empty('No rows had case text.'), caseColumn, skipped }
  return {
    cases,
    caseColumn,
    areaColumn: areaIndex >= 0 ? headers[areaIndex] : null,
    skipped,
    error: null,
  }
}
